'use client';

import LocalizedClientLink from '@modules/common/components/localized-client-link';
import type { ProductTabsVM } from '../../../../types/view-models';
import Accordion from './accordion';

type ProductTabsProps = {
  vm: ProductTabsVM;
};

const hasDimensions = (vm: ProductTabsVM): boolean =>
  [vm.length, vm.width, vm.height].every((v) => v != null);

const hasProductInfo = (vm: ProductTabsVM): boolean =>
  !!vm.typeValue?.trim() || vm.weight != null || hasDimensions(vm);

const ProductTabs = ({ vm }: ProductTabsProps) => {
  const tabs = [
    ...(hasProductInfo(vm)
      ? [
          {
            label: 'Product Information',
            key: 'product-information',
            component: <ProductInfoTab vm={vm} />,
          },
        ]
      : []),
    {
      label: 'Shipping & Returns',
      key: 'shipping-returns',
      component: <ShippingInfoTab />,
    },
  ];

  return (
    <div className="w-full">
      <Accordion type="multiple">
        {tabs.map((tab) => (
          <Accordion.Item
            key={tab.key}
            title={tab.label}
            className="border-brand-primary-100 py-[18px]"
            headingSize="medium"
            value={tab.key}
          >
            {tab.component}
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};

const ProductInfoTab = ({ vm }: ProductTabsProps) => {
  const hasDimensions =
    vm.length != null && vm.width != null && vm.height != null;

  return (
    <div className="text-base py-6 leading-[1.5] text-brand-primary-900">
      <div className="flex flex-col gap-y-4">
        {vm.typeValue?.trim() && (
          <div>
            <span className="font-bold">Type</span>
            <p className="text-brand-primary-800">{vm.typeValue.trim()}</p>
          </div>
        )}
        {vm.weight != null && (
          <div>
            <span className="font-bold">Weight</span>
            <p className="text-brand-primary-800">{vm.weight} g</p>
          </div>
        )}
        {hasDimensions && (
          <div>
            <span className="font-bold">Dimensions</span>
            <p className="text-brand-primary-800">
              {vm.length}L x {vm.width}W x {vm.height}H
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const ShippingInfoTab = () => {
  return (
    <div className="text-base py-6 leading-[1.5] text-brand-primary-900">
      <div className="grid grid-cols-1 gap-y-4">
        <div>
          <span className="font-bold">Free delivery</span>
          <p className="text-brand-primary-800">
            Free standard delivery on orders over &pound;40. Orders are
            dispatched Monday&ndash;Friday and typically arrive in 1&ndash;2
            business days.
          </p>
        </div>
        <div>
          <span className="font-bold">Returns</span>
          <p className="text-brand-primary-800">
            For full details on our returns policy, visit our{' '}
            <LocalizedClientLink
              href="/shipping"
              className="underline hover:no-underline"
            >
              delivery &amp; returns page
            </LocalizedClientLink>
            .
          </p>
        </div>
        <div>
          <span className="font-bold">Need help?</span>
          <p className="text-brand-primary-800">
            Our pharmacy team is here to help. Get in touch if you have any
            questions about your order or need advice on a product.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductTabs;
